<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type PartialApplicationData = App.Students.Data.PartialApplicationData;
type ApplicationData = App.Students.Data.ApplicationData;
type StudentUserData = App.Auth.Data.StudentUserData;

import CollegeApplicationDataRequestForm from './CollegeApplicationDataRequestForm.vue';
import DataRequestDialog from '@/domains/data-request/components/DataRequestDialog.vue';

const props = defineProps<{
  application: ApplicationData;
  user: StudentUserData;
  statuses: EnumData[];
  dataRequest?: DataRequestData;
}>();

const dataRequestAttributes = computed(
  () => props.dataRequest?.attributes as PartialApplicationData | undefined
);

const formUrl = computed(() => {
  if (props.dataRequest?.status.value === 'pending') {
    return route('student.data-requests.college-application.update', {
      dataRequest: props.dataRequest.id
    });
  }
  return route('student.data-requests.college-application.store');
});

const form = useForm({
  url: formUrl.value,
  method: 'POST',
  preserveState: false,
  fields: {
    application: {
      student_id: props.user.id ?? null,
      institute_id:
        buildSelectableInstitute(
          dataRequestAttributes.value?.institute || props.application?.institute
        ) ?? null,
      status: dataRequestAttributes.value?.status || props.application?.status,
      intend_to_enroll:
        dataRequestAttributes.value?.intend_to_enroll ?? props.application?.intend_to_enroll,
      has_waiver: dataRequestAttributes.value?.has_waiver ?? props.application?.has_waiver,
      waiver: dataRequestAttributes.value?.waiver || props.application?.waiver,
      first_choice: dataRequestAttributes.value?.first_choice ?? props.application?.first_choice,
      sent_hs_transcript:
        dataRequestAttributes.value?.sent_hs_transcript ?? props.application?.sent_hs_transcript,
      financial_aid_submitted_to_school:
        dataRequestAttributes.value?.financial_aid_submitted_to_school ??
        props.application?.financial_aid_submitted_to_school,
      early_action: dataRequestAttributes.value?.early_action ?? props.application?.early_action,
      early_decision:
        dataRequestAttributes.value?.early_decision ?? props.application?.early_decision,
      applied_to_honors_program:
        dataRequestAttributes.value?.applied_to_honors_program ??
        props.application?.applied_to_honors_program
    },
    user_comment: props.dataRequest?.user_comment ?? ''
  },
  transform: (fields) => ({
    ...fields,
    requestable_id: props.application?.id,
    application: {
      student_id: fields.application.student_id,
      institute_id: fields.application.institute_id?.value,
      status: fields.application.status?.value,
      intend_to_enroll: fields.application.intend_to_enroll,
      has_waiver: fields.application.has_waiver,
      waiver: fields.application.waiver,
      first_choice: fields.application.first_choice,
      sent_hs_transcript: fields.application.sent_hs_transcript,
      financial_aid_submitted_to_school: fields.application.financial_aid_submitted_to_school,
      early_action: fields.application.early_action,
      early_decision: fields.application.early_decision,
      applied_to_honors_program: fields.application.applied_to_honors_program
    }
  })
});
</script>

<template>
  <DataRequestDialog dataRequestType="College Applications" :form="form">
    <template #form>
      <CollegeApplicationDataRequestForm
        :dataRequest="dataRequest"
        :form="form"
        :application="application"
        :statuses="statuses"
      />
    </template>
  </DataRequestDialog>
</template>
